import { type RefObject, useEffect, type ReactNode } from 'react';
import {
  RECIPE_COUNT_DISPLAY,
  RECIPE_COUNT_ABBREVIATED_LOWER,
} from '@hungryroot/constants';
import {
  Heading,
  Bank,
  ImproveHealth,
  LoseWeight,
  LowerStress,
  SaveMoney,
  SaveTime,
  SimplifyCooking,
  Sun,
  WasteLessFood,
} from '@hungryroot/ui-kit';
import { Goals, type Quiz } from '../../defs/quiz';
import { ValuePropComponentTheme } from '../../defs/valueProp';
import { setSavedNavState } from '../../navigation/navigation';
import { ValuePropCard } from '../ValuePropCard/ValuePropCard';
import { ValuePropLayout } from '../ValuePropLayout/ValuePropLayout';
import styles from './GoalsBenefits.module.css';
type Props = {
  next: string | null;
  prev: string | null;
  quiz: Quiz;
  scrollRef: RefObject<HTMLDivElement>;
};

type CardContent = {
  text: ReactNode;
  icon: ReactNode;
};

function getHighestPriorityGoalsCards<T extends number>(
  goals: T[] | null,
  cardPriorityMap: {
    [key: number]: CardContent;
  },
  goalsPriorityMap: { [key: number]: T }
): CardContent[] {
  if (goals === null) {
    return [];
  }

  const selectedGoalsByPriority = goals.map(
    (goal) => goalsPriorityMap[goal as keyof typeof goalsPriorityMap]
  );

  const sortedSelectedGoalsByPriority = Array.from(
    new Set(selectedGoalsByPriority)
  ).sort((a, b) => a - b);

  // If only 1 goal selected, render the card for that goal along with the highest priority card, in order of priority
  if (sortedSelectedGoalsByPriority.length < 2) {
    const nextHighestPriority = Object.keys(cardPriorityMap).filter(
      (priority) => parseInt(priority) !== sortedSelectedGoalsByPriority[0]
    )[0];

    return [
      ...sortedSelectedGoalsByPriority?.map(
        (priority) => cardPriorityMap[priority as keyof typeof cardPriorityMap]
      ),
      cardPriorityMap[
        Number(nextHighestPriority) as keyof typeof cardPriorityMap
      ],
    ].filter((card) => card !== undefined);
  }

  // If multiple goals selected, render the cards for the 2 highest priority goals, in order of priority
  const highestPriorities = sortedSelectedGoalsByPriority.slice(0, 2);

  return highestPriorities
    ?.map(
      (priority) => cardPriorityMap[priority as keyof typeof cardPriorityMap]
    )
    .filter((card) => card !== undefined);
}

const GOALS_PRIORITY_MAP = {
  [Goals.ImproveHealth]: 1,
  [Goals.SaveMoney]: 2,
  [Goals.LoseWeight]: 3,
  [Goals.SaveTime]: 4,
  [Goals.LowerStress]: 5,
  [Goals.SimplifyCooking]: 6,
  [Goals.GroceryShopLess]: 7,
  [Goals.WasteLessFood]: 8,
  [Goals.EatLessTakeout]: 9,
};

function getCardContentPriorityMap<S extends Record<string, string>>(
  styles: S
): {
  [key: number]: CardContent;
} {
  return {
    1: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          {RECIPE_COUNT_DISPLAY} easy recipes{' '}
          <span className={styles['text-transparent']}>
            that fit your needs, from high protein to gut-friendly to
            anti-inflammatory.
          </span>
        </Heading>
      ),
      icon: <ImproveHealth className={styles.icon} />,
    },
    2: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          74% of customers save money{' '}
          <span className={styles['text-transparent']}>
            by ditching takeout, reducing food waste, and fewer trips to the
            store.
          </span>
        </Heading>
      ),
      icon: <Bank className={styles.icon} />,
    },
    3: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          Join the 200k+ people who have lost weight{' '}
          <span className={styles['text-transparent']}>
            with our portion controlled, filling and easy recipes.
          </span>
        </Heading>
      ),
      icon: <LoseWeight className={styles.icon} />,
    },
    4: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          <span className={styles['text-transparent']}>
            Hungryroot customers{' '}
          </span>
          save 3 hours each week{' '}
          <span className={styles['text-transparent']}>
            on meal planning, shopping, and cooking.
          </span>
        </Heading>
      ),
      icon: <SaveTime className={styles.icon} />,
    },
    5: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          <span className={styles['text-transparent']}>
            We’ll take care of food for you. That’s why{' '}
          </span>
          96% of customers report lower stress{' '}
          <span className={styles['text-transparent']}>after joining. </span>
        </Heading>
      ),
      icon: <LowerStress className={styles.icon} />,
    },
    6: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          <span className={styles['text-transparent']}>
            Cooking is simple with Hungryroot’s
          </span>{' '}
          15 minute recipes,{' '}
          <span className={styles['text-transparent']}>
            plus chef-quality, ready-to-eat meals.
          </span>
        </Heading>
      ),
      icon: <SimplifyCooking className={styles.icon} />,
    },
    7: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          Skip the lines, and crowded aisles –{' '}
          <span className={styles['text-transparent']}>
            shop over {RECIPE_COUNT_ABBREVIATED_LOWER} easy recipes and healthy
            groceries with a tap.
          </span>
        </Heading>
      ),
      icon: <Sun className={styles.icon} />,
    },
    8: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          <span className={styles['text-transparent']}>
            ~40% of food in the US goes to waste. But{' '}
          </span>
          with a personalized plan, your food gets put to tasty use.
        </Heading>
      ),
      icon: <WasteLessFood className={styles.icon} />,
    },
    9: {
      text: (
        <Heading variant="H4" className={styles['card-heading']}>
          <span className={styles['text-transparent']}>
            Takeout adds up fast in cost and calories.
          </span>{' '}
          Save $200+ a month{' '}
          <span className={styles['text-transparent']}>
            with quick, healthy meals.
          </span>
        </Heading>
      ),
      icon: <SaveMoney className={styles.icon} />,
    },
  };
}

export function GoalsBenefits({ next, prev, quiz, scrollRef }: Props) {
  const { goals: customerGoals } = quiz;
  const theme = ValuePropComponentTheme.Avocado;
  const variantCardContent = getCardContentPriorityMap(styles);

  const cardContent =
    getHighestPriorityGoalsCards(
      customerGoals,
      variantCardContent,
      GOALS_PRIORITY_MAP
    ) || {};

  useEffect(() => {
    if (next !== null) {
      setSavedNavState(next);
    }
  }, [next]);

  if (cardContent === undefined) {
    return;
  }

  return (
    <ValuePropLayout
      headerText="Hungryroot makes it easy to achieve your goals"
      theme={theme}
      cards={cardContent.map(({ text, icon }, i) => (
        <ValuePropCard
          testid={`goals-value-prop-card-${i}`}
          key={i}
          text={text}
          icon={icon}
          theme={theme}
          className={styles['card']}
        />
      ))}
      next={next}
      prev={prev}
      scrollRef={scrollRef}
    />
  );
}
